/**
 * Field-level primitives shared by every request schema.
 *
 * They exist so the same rule is not restated per resource — §9's URL rule in
 * particular, which is easy to write once and forget on the fourth table that
 * grows a link column.
 */

import { z } from 'zod';

export const uuid = z.string().uuid();

/** A `date` column: `YYYY-MM-DD`, no time, no zone. */
export const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'not a real date');

/** A `timestamptz` column, ISO-8601. Stored UTC (§9). */
export const timestampString = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'not an ISO-8601 timestamp');

/**
 * Money — `numeric(14,4)` everywhere (§5).
 *
 * Accepted as a number or a string and carried as a STRING, because `node-pg`
 * returns `numeric` as a string and §9 says money is never a float. Sending a
 * JS number for a 14,4 value is lossy above 2^53 / 10^4; accepting a string is
 * how a client sends an exact one.
 */
export const money = z.union([z.number().finite(), z.string().regex(/^-?\d{1,10}(\.\d{1,4})?$/)]);

export const nonNegativeMoney = money.refine((v) => Number(v) >= 0, 'must not be negative');

/** ISO 3166-1 alpha-2, matching the check constraint. */
export const countryCode = z.string().regex(/^[A-Z]{2}$/, 'expected an ISO 3166-1 alpha-2 code');

/**
 * §9: URL fields are validated against `^https://`, rendered
 * `rel="noopener noreferrer"`, and never `javascript:` / `data:`. **No
 * server-side component dereferences a user-supplied URL** (SSRF).
 */
export const httpsUrl = z
  .string()
  .max(2048)
  .regex(/^https:\/\//, 'must be an https:// URL');

/** Free text with a ceiling. Unbounded text is what makes a 6 MB body reachable. */
export const shortText = z.string().max(500);
export const mediumText = z.string().max(4000);
export const longText = z.string().max(20_000);

/** IANA zone. Validated by asking the platform rather than shipping a list. */
export const ianaTimezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'not an IANA time zone');

/** `T | null`, but not `undefined` — the shape a nullable column takes in a PATCH body. */
export function nullable<T extends z.ZodTypeAny>(inner: T) {
  return inner.nullable();
}
